import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { brandsAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Sparkles, Store, Package, DollarSign, Upload, FileUp, LogOut } from 'lucide-react';

const BrandDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await brandsAPI.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard');
      if (error.response?.status === 404) {
        navigate('/brand/register');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGetAIPriceSuggestion = async (productId) => {
    try {
      toast.loading('Getting AI price suggestion...');
      const response = await brandsAPI.getPriceSuggestion(productId);
      toast.success(`AI Suggested Price: $${response.data.suggested_price}`, {
        description: response.data.reasoning.substring(0, 150) + '...'
      });
    } catch (error) {
      toast.error('Failed to get AI suggestion');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A3A52]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Navbar */}
      <nav className="border-b border-[#E5E1DC] bg-white/90 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <Sparkles className="h-7 w-7 text-[#1A3A52]" strokeWidth={1.5} />
              <span className="text-2xl font-serif font-semibold text-[#2C3E50]">LuxLyx</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                data-testid="bulk-upload-btn"
                onClick={() => navigate('/brand/bulk-upload')}
                variant="outline"
                className="border-[#E5E1DC] text-[#5A6C7D] hover:bg-[#F5F3F0]"
              >
                <FileUp className="h-5 w-5 mr-2" strokeWidth={1.5} />
                Bulk Upload
              </Button>
              <Button
                data-testid="upload-product-btn"
                onClick={() => navigate('/brand/upload')}
                className="bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium"
              >
                <Upload className="h-5 w-5 mr-2" strokeWidth={1.5} />
                Upload Product
              </Button>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                onClick={handleLogout}
                className="text-[#5A6C7D] hover:text-[#1A3A52] hover:bg-[#F5F3F0]"
              >
                <LogOut className="h-5 w-5" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        {/* Brand Info */}
        <div className="bg-white border border-[#E5E1DC] rounded-xl p-8 mb-8 shadow-sm">
          <div className="flex items-center space-x-4 mb-4">
            <Store className="h-10 w-10 text-[#1A3A52]" strokeWidth={1.5} />
            <div>
              <h1 className="text-3xl font-serif font-semibold text-[#2C3E50]">{dashboard.brand.brand_name}</h1>
              <p className="text-[#5A6C7D]">{dashboard.brand.description}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 mt-6">
            <div>
              <span className="text-[#8B9AAC] text-sm uppercase tracking-wide">Status</span>
              <div className={`font-medium mt-1 ${
                dashboard.brand.status === 'approved' ? 'text-[#1A3A52]' : 'text-[#8B9AAC]'
              }`}>
                {dashboard.brand.status.charAt(0).toUpperCase() + dashboard.brand.status.slice(1)}
              </div>
            </div>
            <div>
              <span className="text-[#8B9AAC] text-sm uppercase tracking-wide">Discount Range</span>
              <div className="font-medium text-[#2C3E50] mt-1">{dashboard.brand.discount_min}% - {dashboard.brand.discount_max}%</div>
            </div>
            <div>
              <span className="text-[#8B9AAC] text-sm uppercase tracking-wide">Commission</span>
              <div className="font-medium text-[#2C3E50] mt-1">{dashboard.brand.commission_rate}%</div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-[#E5E1DC] rounded-xl p-6 shadow-sm">
            <Package className="h-10 w-10 text-[#1A3A52] mb-3" strokeWidth={1.5} />
            <div className="text-3xl font-serif font-semibold text-[#2C3E50]">{dashboard.products.length}</div>
            <div className="text-[#5A6C7D] text-sm uppercase tracking-wide mt-1">Products</div>
          </div>
          <div className="bg-white border border-[#E5E1DC] rounded-xl p-6 shadow-sm">
            <DollarSign className="h-10 w-10 text-[#1A3A52] mb-3" strokeWidth={1.5} />
            <div className="text-3xl font-serif font-semibold text-[#2C3E50]">${dashboard.total_sales.toFixed(2)}</div>
            <div className="text-[#5A6C7D] text-sm uppercase tracking-wide mt-1">Total Sales</div>
          </div>
          <div className="bg-white border border-[#E5E1DC] rounded-xl p-6 shadow-sm">
            <Store className="h-10 w-10 text-[#1A3A52] mb-3" strokeWidth={1.5} />
            <div className="text-3xl font-serif font-semibold text-[#2C3E50]">{dashboard.orders.length}</div>
            <div className="text-[#5A6C7D] text-sm uppercase tracking-wide mt-1">Orders</div>
          </div>
        </div>

        {/* Products */}
        <h2 className="text-2xl font-serif font-semibold mb-6 text-[#2C3E50]">Your Products</h2>
        {dashboard.products.length === 0 ? (
          <div className="text-center py-20 bg-white border border-[#E5E1DC] rounded-xl">
            <Package className="h-16 w-16 text-[#8B9AAC] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#5A6C7D] text-lg mb-6">No products yet</p>
            <Button
              data-testid="upload-first-product-btn"
              onClick={() => navigate('/brand/upload')}
              className="bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium"
            >
              Upload Your First Product
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {dashboard.products.map((product) => (
              <div
                key={product.id}
                data-testid={`product-${product.id}`}
                className="bg-white border border-[#E5E1DC] rounded-xl p-6 shadow-sm flex items-start space-x-6"
              >
                <img
                  src={product.images[0] || 'https://via.placeholder.com/120'}
                  alt={product.name}
                  className="w-32 h-32 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <h3 className="text-xl font-serif font-semibold mb-2 text-[#2C3E50]">{product.name}</h3>
                  <p className="text-[#5A6C7D] mb-3 line-clamp-2">{product.description}</p>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-[#8B9AAC]">MRP:</span>
                      <div className="font-medium text-[#2C3E50]">${product.mrp}</div>
                    </div>
                    <div>
                      <span className="text-[#8B9AAC]">Sale Price:</span>
                      <div className="font-medium text-[#1A3A52]">${product.current_price}</div>
                    </div>
                    <div>
                      <span className="text-[#8B9AAC]">Stock:</span>
                      <div className="font-medium text-[#2C3E50]">{product.stock}</div>
                    </div>
                    <div>
                      <span className="text-[#8B9AAC]">Status:</span>
                      <div className={`font-medium ${
                        product.status === 'approved' ? 'text-[#1A3A52]' : 'text-[#8B9AAC]'
                      }`}>
                        {product.status}
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  data-testid={`ai-price-${product.id}`}
                  onClick={() => handleGetAIPriceSuggestion(product.id)}
                  variant="outline"
                  className="border-[#E5E1DC] text-[#5A6C7D] hover:bg-[#F5F3F0]"
                >
                  <Sparkles className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  AI Price
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandDashboard;
